import React, { useState, useEffect } from 'react'
import { 
  Package,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  BarChart3,
  Filter,
  Download,
  Warehouse,
  ShoppingCart
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import inventoryReportsService from '../../services/inventoryReportsService'
import DataTable from '../../components/DataTable/DataTable'

const InventoryReports = () => {
  const navigate = useNavigate()
  const [reportsData, setReportsData] = useState(null)
  const [filterOptions, setFilterOptions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    category: '',
    productId: '',
    warehouse: '',
    lowStockThreshold: ''
  })

  useEffect(() => {
    loadFilterOptions()
    loadReportsData()
  }, [])

  useEffect(() => {
    loadReportsData()
  }, [filters])

  const loadFilterOptions = async () => {
    try {
      const response = await inventoryReportsService.getInventoryFilterOptions()
      if (response.success) {
        setFilterOptions(response.data)
        // Set default date range (last 12 months)
        const endDate = new Date()
        const startDate = new Date()
        startDate.setMonth(startDate.getMonth() - 12)
        setFilters(prev => ({
          ...prev,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }))
      }
    } catch (error) {
      console.error('Error loading filter options:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load filter options')
      }
    }
  }

  const loadReportsData = async () => {
    try {
      setLoading(true)
      const response = await inventoryReportsService.getInventoryReports(filters)
      if (response.success) {
        setReportsData(response.data)
      } else {
        toast.error(response.message || 'Failed to load inventory reports')
      }
    } catch (error) {
      console.error('Error loading inventory reports:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load inventory reports')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleResetFilters = () => {
    const endDate = new Date()
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - 12)
    setFilters({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      category: '',
      productId: '',
      warehouse: '',
      lowStockThreshold: ''
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0)
  }

  const getStockStatusBadge = (status) => {
    const statusMap = {
      'In Stock': { color: '#28a745', bg: '#d4edda' },
      'Low Stock': { color: '#ffc107', bg: '#fff3cd' },
      'Reorder': { color: '#dc3545', bg: '#f8d7da' },
      'Unknown': { color: 'var(--color-text-secondary, #5B6478)', bg: '#e9ecef' }
    }
    const style = statusMap[status] || statusMap['Unknown']
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500',
        color: style.color,
        backgroundColor: style.bg
      }}>
        {status}
      </span>
    )
  }

  // Product Inventory Table Columns
  const productInventoryColumns = [
    {
      accessorKey: 'productName',
      header: 'Product',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: '500' }}>{getValue() || '-'}</div>
          {row.original.productCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.productCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'categoryName',
      header: 'Category',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left' }}>
          {getValue() || 'Uncategorized'}
        </div>
      ),
    },
    {
      accessorKey: 'currentStock',
      header: 'Current Stock',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatNumber(getValue())} {row.original.unitOfMeasurement || ''}
        </div>
      ),
    },
    {
      accessorKey: 'minimumStock',
      header: 'Min. Stock',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())} {row.original.unitOfMeasurement || ''}
        </div>
      ),
    },
    {
      accessorKey: 'totalQuantityAccepted',
      header: 'Total Received',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())} {row.original.unitOfMeasurement || ''}
        </div>
      ),
    },
    {
      accessorKey: 'totalValue',
      header: 'Total Value',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'stockStatus',
      header: 'Status',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {getStockStatusBadge(getValue())}
        </div>
      ),
    },
  ]

  // Low Stock Items Table Columns
  const lowStockColumns = [
    {
      accessorKey: 'productName',
      header: 'Product',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontWeight: '500' }}>{getValue() || '-'}</div>
          {row.original.productCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.productCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'currentStock',
      header: 'Current Stock',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600', color: '#dc3545' }}>
          {formatNumber(getValue())} {row.original.unitOfMeasurement || ''}
        </div>
      ),
    },
    {
      accessorKey: 'minimumStock',
      header: 'Min. Stock',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())} {row.original.unitOfMeasurement || ''}
        </div>
      ),
    },
    {
      accessorKey: 'reorderLevel',
      header: 'Reorder Level',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())} {row.original.unitOfMeasurement || ''}
        </div>
      ),
    },
    {
      accessorKey: 'averageUnitPrice',
      header: 'Avg. Unit Price',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
  ]

  // Category Inventory Table Columns
  const categoryColumns = [
    {
      accessorKey: 'categoryName',
      header: 'Category',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left', fontWeight: '500' }}>
          {getValue() || 'Uncategorized'}
        </div>
      ),
    },
    {
      accessorKey: 'productCount',
      header: 'Products',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalQuantityAccepted',
      header: 'Total Quantity',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalValue',
      header: 'Total Value',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'averageUnitPrice',
      header: 'Avg. Unit Price',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
  ]

  // Warehouse Inventory Table Columns
  const warehouseColumns = [
    {
      accessorKey: '_id',
      header: 'Warehouse',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'left', fontWeight: '500' }}>
          {getValue() || 'Unknown'}
        </div>
      ),
    },
    {
      accessorKey: 'productCount',
      header: 'Products',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalQuantityAccepted',
      header: 'Total Quantity',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'totalValue',
      header: 'Total Value',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {formatCurrency(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'grnCount',
      header: 'GRNs',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {formatNumber(getValue())}
        </div>
      ),
    },
  ]

  const breadcrumbItems = [
    { label: 'Procurement', path: '/procurement' },
    { label: 'Reports', path: '/procurement?tab=reports' },
    { label: 'Inventory Reports', isCurrent: true }
  ]

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Inventory Reports</h1>
          <p>Inventory levels and stock analysis</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Filter size={18} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Filters</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Category
            </label>
            <select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                background: '#fff'
              }}
            >
              <option value="">All Categories</option>
              {filterOptions?.categories?.map(cat => (
                <option key={cat._id} value={cat._id}>
                  {cat.categoryName} {cat.categoryCode ? `(${cat.categoryCode})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
              Warehouse
            </label>
            <select
              name="warehouse"
              value={filters.warehouse}
              onChange={handleFilterChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                background: '#fff'
              }}
            >
              <option value="">All Warehouses</option>
              {filterOptions?.warehouses?.map(wh => (
                <option key={wh} value={wh}>
                  {wh}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
            <button
              onClick={handleResetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading inventory reports...</p>
        </div>
      ) : reportsData ? (
        <>
          <div className="dashboard-metrics-grid">
            <div className="metric-card deals">
              <div className="metric-header">
                <div className="metric-icon">
                  <Package size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>Received</span>
                </div>
              </div>
              <div className="metric-value">{formatNumber(reportsData.inventoryStats.totalItemsReceived)}</div>
              <div className="metric-title">Total Items Received</div>
              <div className="metric-subtitle">Goods logged in</div>
            </div>

            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon">
                  <CheckCircle size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>Accepted</span>
                </div>
              </div>
              <div className="metric-value">{formatNumber(reportsData.inventoryStats.totalItemsAccepted)}</div>
              <div className="metric-title">Total Items Accepted</div>
              <div className="metric-subtitle">Quality approved</div>
            </div>

            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon">
                  <DollarSign size={16} />
                </div>
                <div className="metric-trend positive">
                  <span>Value</span>
                </div>
              </div>
              <div className="metric-value">{formatCurrency(reportsData.inventoryStats.totalValue)}</div>
              <div className="metric-title">Total Inventory Value</div>
              <div className="metric-subtitle">Stock valuation</div>
            </div>

            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon">
                  <AlertTriangle size={16} />
                </div>
                <div className="metric-trend negative">
                  <span>Alert</span>
                </div>
              </div>
              <div className="metric-value">{formatNumber(reportsData.lowStockItems?.length || 0)}</div>
              <div className="metric-title">Low Stock Items</div>
              <div className="metric-subtitle">Needs attention</div>
            </div>
          </div>

          {/* Stock Status Summary */}
          {reportsData.stockStatusSummary && (
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Stock Status Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '16px', background: '#d4edda', borderRadius: '6px', border: '1px solid #c3e6cb' }}>
                  <div style={{ fontSize: '13px', color: '#155724', marginBottom: '8px' }}>In Stock</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#155724' }}>
                    {formatNumber(reportsData.stockStatusSummary.inStock)}
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#fff3cd', borderRadius: '6px', border: '1px solid #ffeaa7' }}>
                  <div style={{ fontSize: '13px', color: '#856404', marginBottom: '8px' }}>Low Stock</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#856404' }}>
                    {formatNumber(reportsData.stockStatusSummary.lowStock)}
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f8d7da', borderRadius: '6px', border: '1px solid #f5c6cb' }}>
                  <div style={{ fontSize: '13px', color: '#721c24', marginBottom: '8px' }}>Reorder Required</div>
                  <div style={{ fontSize: '20px', fontWeight: '600', color: '#721c24' }}>
                    {formatNumber(reportsData.stockStatusSummary.reorder)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Low Stock Items */}
          {reportsData.lowStockItems && reportsData.lowStockItems.length > 0 && (
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#dc3545' }}>
                Low Stock Items ({reportsData.lowStockItems.length})
              </h3>
              <DataTable
                data={reportsData.lowStockItems}
                columns={lowStockColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                pageSize={10}
              />
            </div>
          )}

          {/* Product Inventory */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Product Inventory</h3>
            <DataTable
              data={reportsData.productInventory || []}
              columns={productInventoryColumns}
              loading={false}
              enableSelection={false}
              noCard={true}
              pageSize={10}
            />
          </div>

          {/* Category Analysis */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Category-wise Inventory</h3>
            <DataTable
              data={reportsData.categoryInventory || []}
              columns={categoryColumns}
              loading={false}
              enableSelection={false}
              noCard={true}
              pageSize={10}
            />
          </div>

          {/* Warehouse Analysis */}
          {reportsData.warehouseInventory && reportsData.warehouseInventory.length > 0 && (
            <div style={{
              background: '#fff',
              border: '1px solid #e9ecef',
              borderRadius: '8px',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Warehouse-wise Inventory</h3>
              <DataTable
                data={reportsData.warehouseInventory}
                columns={warehouseColumns}
                loading={false}
                enableSelection={false}
                noCard={true}
                pageSize={10}
              />
            </div>
          )}

          {/* Stock Movement Trends Chart Placeholder */}
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Stock Movement Trends (Last 12 Months)</h3>
            <div style={{
              height: '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f9fafb',
              borderRadius: '6px',
              color: 'var(--color-text-secondary, #5B6478)'
            }}>
              Chart visualization would be displayed here
              <br />
              <small>Monthly inventory received and accepted trends</small>
            </div>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-text-secondary, #5B6478)' }}>
          No data available
        </div>
      )}
    </div>
  )
}

export default InventoryReports

